import { useState } from "react";
import { AutocompleteOption } from "@miplatform/components";
import {
  ALL,
  ENTITY_NAMES,
  HTTP_STATUSES,
  ORDER_STATUS_DISPLAY,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  AccountType,
  IOrderTemplateResponse,
  OrderStatus,
  PlatformModules,
} from "@miplatform/types";
import { createUUID, sortOnField } from "@miplatform/utils";
import {
  useAccounts,
  useCommunities,
  useOrderTemplates,
} from "@mibuildweb/hooks";
import {
  ORDERS_SEARCH_FIELDS,
  ORDERS_SEARCH_GROUP_AND_SORT_BY_OPTIONS,
} from "../OrdersSearch.constants";

const getOrderTemplatesUUID = createUUID();

export const useOrderFilters = () => {
  const [orderTemplateOptions, setOrderTemplateOptions] = useState<
    AutocompleteOption[]
  >([]);
  const { accounts, getAccounts } = useAccounts({
    accountTypes: [AccountType.Builder, AccountType.Service],
  });
  const { communities, getCommunities } = useCommunities();
  const stateManager = useComponentState();

  const { getOrderTemplates } = useOrderTemplates();

  const getSchedulingOrderTemplates = () => {
    stateManager.abort(getOrderTemplatesUUID);
    stateManager.run(async () => {
      const response = await getOrderTemplates({
        sort: "name",
        filters: { module: { eq: PlatformModules.MiScheduling } },
      });

      if (response.status === HTTP_STATUSES.OK) {
        const templatesOptions: AutocompleteOption[] = [
          { label: ALL, value: ALL },
          ...(response.data as IOrderTemplateResponse[]).map((template) => ({
            label: template.name,
            value: template.id,
            ...template,
          })),
        ];

        setOrderTemplateOptions(templatesOptions);
      }
    }, getOrderTemplatesUUID);
  };

  const orderStatusOptions: AutocompleteOption[] = [
    { label: ALL, value: ALL },
    ...Object.values(OrderStatus)
      .map((status) => ({
        label: ORDER_STATUS_DISPLAY[status],
        value: status,
      }))
      .sort(sortOnField("label")),
  ];

  const dataFilters = {
    [ORDERS_SEARCH_FIELDS.BUILDER_ID]: {
      field: ORDERS_SEARCH_FIELDS.BUILDER_ID,
      label: ENTITY_NAMES.BUILDER,
      data: accounts,
      allOption: true,
      valueKey: "id",
      labelKey: "name",
      filterKeys: ["name"],
      getData: getAccounts,
    },
    [ORDERS_SEARCH_FIELDS.COMMUNITY_ID]: {
      field: ORDERS_SEARCH_FIELDS.COMMUNITY_ID,
      label: ENTITY_NAMES.COMMUNITY,
      data: communities,
      allOption: true,
      valueKey: "id",
      labelKey: "name",
      filterKeys: ["name"],
      getData: getCommunities,
    },
    [ORDERS_SEARCH_FIELDS.ORDER_TEMPLATE_ID]: {
      field: ORDERS_SEARCH_FIELDS.ORDER_TEMPLATE_ID,
      label: ENTITY_NAMES.ORDER_TEMPLATE,
      data: orderTemplateOptions,
    },
    [ORDERS_SEARCH_FIELDS.ORDER_STATUS]: {
      field: ORDERS_SEARCH_FIELDS.ORDER_STATUS,
      label: `${ENTITY_NAMES.ORDER} Status`,
      data: orderStatusOptions,
    },
  };

  return {
    groupByOptions: ORDERS_SEARCH_GROUP_AND_SORT_BY_OPTIONS,
    sortByOptions: ORDERS_SEARCH_GROUP_AND_SORT_BY_OPTIONS,
    dataFilters,
    isLoading: stateManager.isBusy(),
    getOrdersSearchDataFilters: getSchedulingOrderTemplates,
  };
};
